// "Your colours become the theme" (LOOK_CONTRACT.md, 22 Oct 2026). The
// business's main and accent colours replace the theme's own colour roles
// (header, footer, buttons, headings, links, bands) in one of three palettes:
// light (default), rich or dark. The theme keeps fonts, shapes and layout.
//
// Contrast is a hard rule. Every use of a chosen colour is checked against
// the surface it sits on and, when it can't reach the threshold, that one use
// is darkened or lightened (up to 10 steps of 12%). The stored brand colour is
// never changed. Pure functions, so tests run under plain node.
import {contrast, inkOn, isHex, luminance, mixHex} from "./colour.js";

export const PALETTES = ["light", "rich", "dark"];
export const WHITE = "#ffffff";
export const BLACK = "#000000";
const DARK_INK = "#14181f";
const DARK_BASE = "#12141a";
const DARK_INK_ON = "#eef1f4";
const DARK_FOOT = "#0b0c10";
const DARK_FOOT_INK = "#c9ced6";
const STEP = 12;
const MAX_STEPS = 10;
export const TEXT_MIN = 4.5;
export const FILL_MIN = 3;

// Move `c` toward `toward` until ok(c) holds (at most 10 steps of 12%).
function nudge(c, ok, toward) {
    let x = c, steps = 0;
    while (!ok(x) && steps < MAX_STEPS) {
        x = mixHex(x, toward, STEP);
        steps += 1;
    }
    return {colour: x, steps};
}
const darkSurface = (surfaces) => surfaces.every((s) => luminance(s) < 0.18);

// Plain-English colour name, for the Look panel's note.
export function colourName(hex) {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min, l = (max + min) / 2;
    if (max < 0.18) return "black";
    if (min > 0.9) return "white";
    if (d < 0.1) return "grey";
    let h;
    if (max === r) h = ((g - b) / d + 6) % 6; else if (max === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
    h *= 60;
    const s = d / (1 - Math.abs(2 * l - 1));
    if (h < 15 || h >= 345) return l > 0.8 ? "pink" : "red";
    if (h < 40) return l < 0.45 && s > 0.3 ? "brown" : "orange";
    if (h < 70) return "yellow";
    if (h < 160) return "green";
    if (h < 200) return "teal";
    if (h < 255) return "blue";
    if (h < 290) return "purple";
    return "pink";
}

// Builds the palette. Returns {vars, notes, checks} or null (the site then
// keeps the theme's own colours): vars are CSS custom properties for <html>,
// notes are the quiet "we nudged" messages, checks are every text/fill pair
// with its threshold (tests assert all of them).
export function paletteColours(t, brand, palette) {
    if (!t || t.legacy || !PALETTES.includes(palette)) return null;
    if (!isHex(brand?.primary) || !isHex(brand?.accent)) return null;
    const main = brand.primary.length === 4 ? "#" + [...brand.primary.slice(1)].map((c) => c + c).join("") : brand.primary;
    const acc = brand.accent.length === 4 ? "#" + [...brand.accent.slice(1)].map((c) => c + c).join("") : brand.accent;
    const dark = palette === "dark";
    const rich = palette === "rich";
    const nudges = [];
    const checks = [];
    const note = (colourOf, original, result, use) => {
        if (result.steps > 0) nudges.push({colour: colourOf, use, how: luminance(result.colour) < luminance(original) ? "darkened" : "lightened", name: colourName(original)});
        return result.colour;
    };
    const check = (name, fg, bg, min) => checks.push({name, fg, bg, min, ratio: contrast(fg, bg)});

    // ---- surfaces ----
    const base = dark ? DARK_BASE : WHITE;
    const inkBase = dark ? DARK_INK_ON : mixHex(DARK_INK, main, 18);
    const ink = dark || contrast(inkBase, WHITE) >= 12 ? inkBase : DARK_INK;
    // A wash of `tgt` over the base, eased back until ink reads on it.
    const wash = (tgt, pct) => {
        for (let k = pct; k > 0; k--) {
            const c = mixHex(base, tgt, k);
            if (contrast(ink, c) >= 10) return c;
        }
        return base;
    };
    const bg = wash(main, dark ? 18 : 4);
    const card = dark ? wash(main, 28) : WHITE;
    const alt = dark ? wash(main, 32) : wash(rich ? acc : main, rich ? 22 : 12);
    const heroTint = dark ? bg : wash(main, 8);
    const surfaces = [bg, card, alt, heroTint];
    // Muted text: the ink eased toward the surface until it still reads on all of them.
    const mutedOn = (inkC, surfs, from = 40) => {
        for (let k = from; k > 0; k -= 5) {
            const m = mixHex(inkC, surfs[0], k);
            if (surfs.every((s) => contrast(m, s) >= TEXT_MIN)) return m;
        }
        return inkC;
    };
    const muted = mutedOn(ink, surfaces);
    const lineBase = dark ? mixHex("#2a2e38", main, 30) : mixHex("#e6e8ec", main, 25);

    // ---- helpers for one use of a colour ----
    const toward = (surfs) => (darkSurface(surfs) ? WHITE : BLACK);
    // Text in `c` on `surfs` (links, headings, icons, eyebrows).
    // When ten steps aren't enough the text takes `fallback` (the area's ink).
    const textOn = (c, surfs, colourOf, use, quiet = false, fallback = ink) => {
        const good = (x) => surfs.every((s) => contrast(x, s) >= TEXT_MIN);
        let r = nudge(c, good, toward(surfs));
        if (!good(r.colour)) r = {colour: fallback, steps: MAX_STEPS};
        return quiet ? r.colour : note(colourOf, c, r, use);
    };
    // A coloured fill that carries text (button, band): inkOn text reads at 4.5,
    // and when `surfs` is given the fill stands out at 3:1 against them.
    const fillOn = (c, surfs, colourOf, use) => {
        const ok = (x) => Math.max(contrast(x, WHITE), contrast(x, DARK_INK)) >= TEXT_MIN && surfs.every((s) => contrast(x, s) >= FILL_MIN);
        const dir = surfs.length ? toward(surfs) : (luminance(c) > 0.2 ? BLACK : WHITE);
        const r = nudge(c, ok, dir);
        return note(colourOf, c, r, use);
    };
    const inkFor = (fill) => inkOn(fill, DARK_INK, WHITE);

    // ---- roles ----
    const btnMainFill = fillOn(rich || dark ? acc : main, [bg, card, alt], rich || dark ? "accent" : "main", "buttons");
    const accText = textOn(acc, surfaces, "accent", "links");
    const heading = dark ? ink : textOn(main, [bg, card, alt], "main", "headings");
    const band = dark ? fillOn(main, [bg], "main", "colour bands") : fillOn(main, [], "main", "colour bands");
    const headerBg = dark ? wash(main, 10) : fillOn(main, [], "main", "header");
    const headerInk = dark ? ink : inkFor(headerBg);
    const heroBg = rich ? fillOn(main, [], "main", "hero") : heroTint;
    const footBg = dark ? DARK_FOOT : fillOn(rich ? mixHex(main, BLACK, 30) : main, [], "main", "footer");
    const footInk = dark ? DARK_FOOT_INK : inkFor(footBg);
    const heroInk = rich ? inkFor(heroBg) : ink;
    const heroSurf = [heroBg];
    const heroMuted = rich ? mutedOn(heroInk, heroSurf, 25) : muted;
    const heroAcc = rich ? textOn(acc, heroSurf, "accent", "links", true, heroInk) : accText;
    // The button on a main-coloured hero must stand out from it.
    let heroBtn = btnMainFill;
    if (rich && contrast(btnMainFill, heroBg) < FILL_MIN) heroBtn = heroInk;
    if (!rich && contrast(btnMainFill, heroBg) < FILL_MIN) heroBtn = fillOn(btnMainFill, [heroBg], rich || dark ? "accent" : "main", "buttons");
    // A button on a band must not vanish into it: the accent when it stands out
    // (3:1) with a readable label, else the band's own ink as the fill.
    const accFill = fillOn(acc, [], "accent", "buttons");
    const buttonOn = (bandBg) => {
        const pref = rich || dark ? btnMainFill : accFill;
        const pick = contrast(accFill, bandBg) >= FILL_MIN ? accFill : (contrast(pref, bandBg) >= FILL_MIN ? pref : inkFor(bandBg));
        return {bg: pick, ink: inkFor(pick)};
    };
    const headBtn = buttonOn(headerBg);
    const footBtn = buttonOn(footBg);
    const headMuted = mutedOn(headerInk, [headerBg], 25);
    const footMuted = mutedOn(footInk, [footBg], 25);
    const headAcc = inkFor(headerBg);
    const footAcc = textOn(acc, [footBg], "accent", "links", true, footInk);

    // ---- everything we promise, as checks ----
    check("ink on bg", ink, bg, TEXT_MIN);
    check("ink on card", ink, card, TEXT_MIN);
    check("ink on alt band", ink, alt, TEXT_MIN);
    check("ink on hero", heroInk, heroBg, TEXT_MIN);
    check("muted on bg", muted, bg, TEXT_MIN);
    check("muted on card", muted, card, TEXT_MIN);
    check("muted on alt band", muted, alt, TEXT_MIN);
    check("muted on hero", heroMuted, heroBg, TEXT_MIN);
    check("link on bg", accText, bg, TEXT_MIN);
    check("link on card", accText, card, TEXT_MIN);
    check("link on alt band", accText, alt, TEXT_MIN);
    check("link on hero", heroAcc, heroBg, TEXT_MIN);
    check("heading on bg", heading, bg, TEXT_MIN);
    check("heading on card", heading, card, TEXT_MIN);
    check("heading on alt band", heading, alt, TEXT_MIN);
    check("button label", inkFor(btnMainFill), btnMainFill, TEXT_MIN);
    check("button fill on bg", btnMainFill, bg, FILL_MIN);
    check("button fill on alt band", btnMainFill, alt, FILL_MIN);
    check("hero button label", inkFor(heroBtn), heroBtn, TEXT_MIN);
    check("hero button fill", heroBtn, heroBg, FILL_MIN);
    check("header button label", headBtn.ink, headBtn.bg, TEXT_MIN);
    check("header button fill vs header", headBtn.bg, headerBg, FILL_MIN);
    check("footer button label", footBtn.ink, footBtn.bg, TEXT_MIN);
    check("footer button fill vs footer", footBtn.bg, footBg, FILL_MIN);
    check("header text", headerInk, headerBg, TEXT_MIN);
    check("header muted", headMuted, headerBg, TEXT_MIN);
    check("footer text", footInk, footBg, TEXT_MIN);
    check("footer muted", footMuted, footBg, TEXT_MIN);
    check("footer link", footAcc, footBg, TEXT_MIN);
    check("colour band text", inkFor(band), band, TEXT_MIN);

    const vars = {
        "--t-bg": bg, "--t-surface": card, "--t-surface-2": alt, "--t-alt-bg": alt,
        "--t-ink": ink, "--t-muted": muted,
        "--t-accent": accText, "--t-accent-ink": inkFor(accText), "--t-link": accText,
        "--t-heading": heading,
        // The ground under light hero words: the band colour if white reads on it, else near-black.
        "--t-light-ground": contrast(WHITE, band) >= 5.5 ? band : "#14161a",
        "--t-primary": band, "--t-primary-ink": inkFor(band),
        "--t-header-bg": headerBg, "--t-header-ink": headerInk, "--t-header-muted": headMuted, "--t-header-accent": headAcc,
        "--t-hero-bg": heroBg, "--t-hero-ink": heroInk, "--t-hero-muted": heroMuted, "--t-hero-accent": heroAcc,
        "--t-header-btn-bg": headBtn.bg, "--t-header-btn-ink": headBtn.ink,
        "--t-footer-btn-bg": footBtn.bg, "--t-footer-btn-ink": footBtn.ink,
        "--t-hero-btn-bg": heroBtn, "--t-hero-btn-ink": inkFor(heroBtn),
        "--t-footer-bg": footBg, "--t-footer-ink": footInk, "--t-footer-muted": footMuted, "--t-footer-accent": footAcc,
        "--theme-text-heading": heading, "--theme-text-body": ink, "--theme-text-muted": muted,
    };
    // A theme's black rules and borders stay on light palettes.
    if (dark || luminance(t.palette.line) >= 0.2) vars["--t-line"] = lineBase;
    // Filled buttons take the palette's button colour; outline and underline
    // buttons keep their own style (their text colour is --t-accent above).
    if (t.button.bg !== "transparent") {
        vars["--t-btn-bg"] = btnMainFill;
        vars["--t-btn-ink"] = inkFor(btnMainFill);
    }
    vars["--t-badge-bg"] = accText;
    vars["--t-badge-ink"] = inkFor(accText);
    return {vars, notes: describeNudges(nudges), checks};
}

const USE_LABEL = {buttons: "buttons", links: "links and icons", headings: "headings", header: "the header", hero: "the top banner", footer: "the footer", "colour bands": "colour bands"};
// "We darkened your yellow a little on buttons so text stays readable."
export function describeNudges(nudges) {
    const seen = new Set();
    const out = [];
    for (const n of nudges) {
        const key = `${n.colour}|${n.use}|${n.how}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({colour: n.colour, use: n.use, text: `We ${n.how} your ${n.name} a little on ${USE_LABEL[n.use] || n.use} so text stays readable.`});
    }
    return out;
}
